import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { API } from '../App';
import { useLanguage } from '../contexts/LanguageContext';
import {
  ArrowLeft, ArrowRight, ChevronLeft, Play, BookOpen, MessageSquare,
  Feather, HelpCircle, X, Volume2, Lightbulb, Maximize2, Minimize2,
  Sun, Moon, StickyNote, Quote, Eye, EyeOff,
} from 'lucide-react';
import axios from 'axios';

/* ============================================================
 * Lesson Player — Classroom Arena
 * Teacher laptop/tablet AND smartboard/projector-ready.
 * Full-stage layouts, projector-safe typography, Present mode.
 * Do NOT touch: returnTo/returnSlide URL param logic, API paths,
 * state shape, data contracts.
 * ============================================================ */

const CEFR_COLORS = {
  'pre-A1': '#2F9E44',
  'A1': '#1971C2',
  'A1-A2': '#F59F00',
  'A2': '#E8590C',
};

const SLIDE_META = {
  title:       { icon: BookOpen,    color: '#1971C2', tint: '#EFF6FF', labelEn: 'Opening',     labelFr: 'Ouverture' },
  vocab:       { icon: Volume2,     color: '#2F9E44', tint: '#F0FDF4', labelEn: 'Vocabulary',  labelFr: 'Vocabulaire' },
  sentences:   { icon: MessageSquare, color: '#7048E8', tint: '#F5F3FF', labelEn: 'Sentences',  labelFr: 'Phrases' },
  grammar:     { icon: Lightbulb,   color: '#E8590C', tint: '#FFF7ED', labelEn: 'Grammar',     labelFr: 'Grammaire' },
  game_launch: { icon: Play,        color: '#C92A2A', tint: '#FEF2F2', labelEn: 'Play',        labelFr: 'Jeu' },
  culture:     { icon: Feather,     color: '#0B7285', tint: '#ECFEFF', labelEn: 'Culture',     labelFr: 'Culture' },
  exit_check:  { icon: HelpCircle,  color: '#862E9C', tint: '#FAF5FF', labelEn: 'Exit Check',  labelFr: 'Sortie' },
};

const GAME_EMOJI = {
  la_course: '🏆', duel_conjugaison: '⚔️', la_tour: '🏗️',
  horloge_folle: '🕐', avalanche: '🏔️', memoire: '🎴',
  tri_express: '🚂', batisseur: '🧱', robot_balayeur: '🤖',
  detective: '🔍', carte_du_canada: '🗺️', le_chef: '👨‍🍳',
};
const GAME_NAMES = {
  la_course: 'La Course', duel_conjugaison: 'Le Duel', la_tour: 'La Tour',
  horloge_folle: "L'Horloge Folle", avalanche: "L'Avalanche", memoire: 'La Mémoire',
  tri_express: 'Tri Express', batisseur: 'Bâtisseur', robot_balayeur: 'Robot Balayeur',
  detective: 'Le Détective', carte_du_canada: 'Carte du Canada', le_chef: 'Le Chef',
};

/* ---------- Global stage styles (injected once) ---------- */
const ARENA_CSS = `
@keyframes lp-slide-in {
  from { opacity: 0; transform: translateY(18px); }
  to   { opacity: 1; transform: translateY(0); }
}
@keyframes lp-pop-in {
  from { opacity: 0; transform: scale(0.92) translateY(10px); }
  to   { opacity: 1; transform: scale(1) translateY(0); }
}
@keyframes lp-glow-pulse {
  0%, 100% { box-shadow: 0 0 0 0 rgba(201,42,42,0.55), 0 12px 36px -8px rgba(201,42,42,0.35); }
  50%      { box-shadow: 0 0 0 14px rgba(201,42,42,0.00), 0 16px 44px -6px rgba(201,42,42,0.45); }
}
@keyframes lp-float {
  0%,100% { transform: translateY(0); }
  50%     { transform: translateY(-6px); }
}
.lp-stage-light { background:
  radial-gradient(1200px 500px at 20% -10%, rgba(113,63,209,0.06), transparent 60%),
  radial-gradient(900px 400px at 110% 10%, rgba(25,113,194,0.05), transparent 60%),
  #FAFAF7;
}
.lp-stage-dark { background:
  radial-gradient(1200px 500px at 20% -10%, rgba(113,63,209,0.18), transparent 60%),
  radial-gradient(900px 400px at 110% 10%, rgba(25,113,194,0.14), transparent 60%),
  #0B1220;
}
.lp-slide-in { animation: lp-slide-in 0.45s cubic-bezier(0.22,1,0.36,1); }
.lp-pop-in { animation: lp-pop-in 0.55s cubic-bezier(0.22,1,0.36,1) both; }
.lp-float { animation: lp-float 4s ease-in-out infinite; }
.lp-launch-pulse { animation: lp-glow-pulse 2.2s ease-in-out infinite; }
.lp-stagger > * { opacity: 0; animation: lp-slide-in 0.5s cubic-bezier(0.22,1,0.36,1) forwards; }
.lp-stagger > *:nth-child(1) { animation-delay: 0.08s; }
.lp-stagger > *:nth-child(2) { animation-delay: 0.16s; }
.lp-stagger > *:nth-child(3) { animation-delay: 0.24s; }
.lp-stagger > *:nth-child(4) { animation-delay: 0.32s; }
.lp-stagger > *:nth-child(5) { animation-delay: 0.40s; }
.lp-stagger > *:nth-child(6) { animation-delay: 0.48s; }
.lp-stagger > *:nth-child(7) { animation-delay: 0.56s; }
.lp-stagger > *:nth-child(8) { animation-delay: 0.64s; }
.lp-stagger > *:nth-child(9) { animation-delay: 0.72s; }
.lp-stagger > *:nth-child(10) { animation-delay: 0.80s; }
.lp-stagger > *:nth-child(11) { animation-delay: 0.88s; }
.lp-stagger > *:nth-child(12) { animation-delay: 0.96s; }

.lp-word-card:hover { transform: translateY(-4px); box-shadow: 0 14px 32px -12px rgba(15,23,42,0.22); }
.lp-word-card { transition: transform 0.2s ease, box-shadow 0.2s ease; }

/* Print: keep only the active slide, clean layout */
@media print {
  .lp-no-print { display: none !important; }
  .lp-stage-light, .lp-stage-dark { background: white !important; }
  .lp-printable { page-break-inside: avoid; }
}
`;

export function LessonPlayer() {
  const { programId, lessonId } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { lang } = useLanguage();
  const [lesson, setLesson] = useState(null);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [showNotes, setShowNotes] = useState(false);
  const [loading, setLoading] = useState(true);
  const [present, setPresent] = useState(false);
  const [dark, setDark] = useState(false);
  const [chromeVisible, setChromeVisible] = useState(true);
  const isFr = lang === 'fr';
  const idleTimer = useRef(null);
  const containerRef = useRef(null);
  // Captured once at construction — survives StrictMode's double-invoke and any later URL stripping.
  const initialSlideRef = useRef(parseInt(new URLSearchParams(window.location.search).get('slide') || '', 10));

  useEffect(() => {
    axios.get(`${API}/programs/lessons/${lessonId}`)
      .then(r => {
        setLesson(r.data);
        const total = r.data?.slides?.length || 0;
        const want = initialSlideRef.current;
        setCurrentSlide(!isNaN(want) && want >= 0 && want < total ? want : 0);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [lessonId]);

  // Strip the ?slide param from the URL once after mount (state has already captured it), so a
  // later refresh/share is clean. Uses a copy — never mutates the live searchParams object.
  useEffect(() => {
    if (searchParams.get('slide') == null) return;
    const cleaned = new URLSearchParams(searchParams);
    cleaned.delete('slide');
    setSearchParams(cleaned, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const totalSlides = lesson?.slides?.length || 0;
  const slide = lesson?.slides?.[currentSlide];

  const goNext = useCallback(() => {
    if (currentSlide < totalSlides - 1) setCurrentSlide(c => c + 1);
  }, [currentSlide, totalSlides]);

  const goPrev = useCallback(() => {
    if (currentSlide > 0) setCurrentSlide(c => c - 1);
  }, [currentSlide]);

  const togglePresent = useCallback(async () => {
    const next = !present;
    setPresent(next);
    try {
      if (next && !document.fullscreenElement) {
        await document.documentElement.requestFullscreen?.();
      } else if (!next && document.fullscreenElement) {
        await document.exitFullscreen?.();
      }
    } catch { /* fullscreen API may be blocked — UI still renders as present overlay */ }
  }, [present]);

  // Keep present state in sync with browser fullscreen changes (Esc)
  useEffect(() => {
    const onFs = () => { if (!document.fullscreenElement) setPresent(false); };
    document.addEventListener('fullscreenchange', onFs);
    return () => document.removeEventListener('fullscreenchange', onFs);
  }, []);

  // Keyboard nav
  useEffect(() => {
    const handler = (e) => {
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return;
      if (e.key === 'ArrowRight' || e.key === ' ') { e.preventDefault(); goNext(); }
      else if (e.key === 'ArrowLeft') { e.preventDefault(); goPrev(); }
      else if (e.key === 'Escape') {
        if (present) { togglePresent(); } else { navigate(`/programs/${programId}`); }
      }
      else if (e.key === 'f' || e.key === 'F') { e.preventDefault(); togglePresent(); }
      else if (e.key === 'd' || e.key === 'D') { setDark(d => !d); }
      else if (e.key === 'n' || e.key === 'N') { setShowNotes(s => !s); }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [goNext, goPrev, navigate, programId, present, togglePresent]);

  // Auto-hide chrome in Present mode
  useEffect(() => {
    if (!present) { setChromeVisible(true); return; }
    const reset = () => {
      setChromeVisible(true);
      if (idleTimer.current) clearTimeout(idleTimer.current);
      idleTimer.current = setTimeout(() => setChromeVisible(false), 2200);
    };
    reset();
    window.addEventListener('mousemove', reset);
    window.addEventListener('keydown', reset);
    window.addEventListener('touchstart', reset);
    return () => {
      if (idleTimer.current) clearTimeout(idleTimer.current);
      window.removeEventListener('mousemove', reset);
      window.removeEventListener('keydown', reset);
      window.removeEventListener('touchstart', reset);
    };
  }, [present]);

  if (loading || !lesson) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', background: '#FAFAF7' }}>
        <div style={{ textAlign: 'center', color: '#64748B' }}>
          <p style={{ fontFamily: 'Outfit, sans-serif', fontSize: '1rem', margin: 0 }}>
            {isFr ? 'Chargement de la leçon…' : 'Loading lesson…'}
          </p>
        </div>
      </div>
    );
  }

  const meta = SLIDE_META[slide?.type] || SLIDE_META.title;
  const MetaIcon = meta.icon;
  const textMain = dark ? '#F8FAFC' : '#0F172A';
  const textMuted = dark ? '#94A3B8' : '#64748B';
  const surface = dark ? 'rgba(255,255,255,0.04)' : 'rgba(255,255,255,0.7)';
  const border = dark ? 'rgba(255,255,255,0.08)' : 'rgba(15,23,42,0.08)';

  // Stage wrapper style — fills viewport in Present mode
  const stageWrapper = present
    ? { position: 'fixed', inset: 0, zIndex: 9999, display: 'flex', flexDirection: 'column' }
    : { height: '100%', display: 'flex', flexDirection: 'column' };

  return (
    <div
      ref={containerRef}
      data-testid="lesson-player"
      className={dark ? 'lp-stage-dark' : 'lp-stage-light'}
      style={stageWrapper}
    >
      <style>{ARENA_CSS}</style>

      {/* ============== TOP CHROME (teacher controls) ============== */}
      <div
        className="lp-no-print"
        style={{
          display: 'flex', alignItems: 'center', gap: '0.75rem',
          padding: '0.6rem 1.25rem',
          background: dark ? 'rgba(15,20,35,0.7)' : 'rgba(255,255,255,0.75)',
          backdropFilter: 'blur(12px)',
          borderBottom: `1px solid ${border}`,
          flexShrink: 0,
          transform: present && !chromeVisible ? 'translateY(-100%)' : 'translateY(0)',
          transition: 'transform 0.28s ease',
          position: present ? 'absolute' : 'relative',
          top: 0, left: 0, right: 0, zIndex: 20,
        }}
      >
        <button
          data-testid="lesson-back-btn"
          onClick={() => navigate(`/programs/${programId}`)}
          style={{
            display: 'flex', alignItems: 'center', gap: 4,
            background: 'none', border: 'none', cursor: 'pointer',
            color: textMuted, fontSize: '0.82rem', fontFamily: 'Nunito, sans-serif', padding: 0,
          }}
        >
          <ChevronLeft size={18} /> {isFr ? 'Programme' : 'Program'}
        </button>

        <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.6rem', minWidth: 0 }}>
          <span style={{
            fontFamily: 'Outfit, sans-serif', fontWeight: 600, fontSize: '0.92rem',
            color: textMain, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
          }}>
            {isFr ? (lesson.title_fr || lesson.title_en) : (lesson.title_en || lesson.title_fr)}
          </span>
          <span style={{
            fontSize: '0.68rem', fontWeight: 700, letterSpacing: '0.03em',
            color: CEFR_COLORS[lesson.cefr_level] || textMuted,
            background: (CEFR_COLORS[lesson.cefr_level] || textMuted) + (dark ? '30' : '18'),
            borderRadius: 999, padding: '2px 9px',
          }}>
            {lesson.cefr_level}
          </span>
          <span style={{ fontSize: '0.72rem', color: textMuted }}>
            {lesson.duration_minutes} min
          </span>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <ChromeButton
            dark={dark}
            onClick={() => setDark(d => !d)}
            title={isFr ? 'Mode scène (D)' : 'Stage mode (D)'}
            testid="lesson-stage-mode-toggle"
          >
            {dark ? <Sun size={15} /> : <Moon size={15} />}
          </ChromeButton>
          <ChromeButton
            dark={dark}
            active={showNotes}
            onClick={() => setShowNotes(s => !s)}
            title={isFr ? 'Notes (N)' : 'Notes (N)'}
            testid="lesson-notes-toggle"
          >
            <StickyNote size={15} /> {!present && <span style={{ fontSize: '0.78rem' }}>{isFr ? 'Notes' : 'Notes'}</span>}
          </ChromeButton>
          <ChromeButton
            dark={dark}
            active={present}
            onClick={togglePresent}
            title={isFr ? 'Présenter (F)' : 'Present (F)'}
            testid="lesson-present-toggle"
          >
            {present ? <Minimize2 size={15} /> : <Maximize2 size={15} />}
            {!present && <span style={{ fontSize: '0.78rem' }}>{isFr ? 'Présenter' : 'Present'}</span>}
          </ChromeButton>
        </div>
      </div>

      {/* ============== SLIDE PROGRESS RAIL ============== */}
      <div
        className="lp-no-print"
        style={{
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          gap: '0.35rem', padding: '0.5rem 0.75rem',
          background: dark ? 'rgba(15,20,35,0.5)' : 'rgba(255,255,255,0.5)',
          backdropFilter: 'blur(8px)',
          borderBottom: `1px solid ${border}`,
          flexShrink: 0,
          transform: present && !chromeVisible ? 'translateY(-100%)' : 'translateY(0)',
          transition: 'transform 0.28s ease 0.04s',
          position: present ? 'absolute' : 'relative',
          top: present ? 52 : 'auto', left: 0, right: 0, zIndex: 19,
        }}
      >
        {lesson.slides.map((s, idx) => {
          const si = SLIDE_META[s.type] || SLIDE_META.title;
          const isActive = idx === currentSlide;
          const isPast = idx < currentSlide;
          return (
            <button
              key={idx}
              data-testid={`slide-dot-${idx}`}
              onClick={() => setCurrentSlide(idx)}
              title={isFr ? si.labelFr : si.labelEn}
              style={{
                width: isActive ? 38 : 10, height: 10, borderRadius: 999,
                background: isActive ? si.color : isPast ? si.color + '80' : (dark ? '#334155' : '#E2E8F0'),
                border: 'none', cursor: 'pointer',
                transition: 'all 0.25s cubic-bezier(0.22,1,0.36,1)',
                padding: 0,
              }}
            />
          );
        })}
      </div>

      {/* ============== STAGE (slide content) ============== */}
      <div style={{
        flex: 1, display: 'flex', overflow: 'hidden', position: 'relative',
        paddingTop: present ? (chromeVisible ? 90 : 0) : 0,
        transition: 'padding-top 0.28s ease',
      }}>
        {/* Category accent glow behind slide */}
        <div
          aria-hidden
          style={{
            position: 'absolute', inset: 0, pointerEvents: 'none',
            background: `radial-gradient(900px 450px at 50% 0%, ${meta.color}${dark ? '22' : '12'}, transparent 65%)`,
            transition: 'background 0.4s ease',
          }}
        />

        {/* Slide content area */}
        <div
          style={{
            flex: 1, overflow: 'auto', padding: 'clamp(1.25rem, 3vw, 3rem) clamp(1.25rem, 5vw, 5rem)',
            display: 'flex', flexDirection: 'column', alignItems: 'center',
            position: 'relative', zIndex: 1,
          }}
        >
          {/* Slide-type badge (category mark) */}
          <div
            className="lp-no-print"
            style={{
              display: 'flex', alignItems: 'center', gap: 10,
              marginBottom: 'clamp(1rem, 2vw, 1.75rem)',
            }}
          >
            <div style={{
              width: 34, height: 34, borderRadius: 10,
              background: meta.color + (dark ? '2A' : '18'),
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <MetaIcon size={17} color={meta.color} />
            </div>
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              <span style={{
                fontFamily: 'Outfit, sans-serif', fontSize: '0.82rem', fontWeight: 700,
                letterSpacing: '0.12em', textTransform: 'uppercase', color: meta.color,
              }}>
                {isFr ? meta.labelFr : meta.labelEn}
              </span>
              <span style={{ fontSize: '0.72rem', color: textMuted, fontWeight: 500 }}>
                {currentSlide + 1} / {totalSlides}
              </span>
            </div>
          </div>

          {/* Slide content (remounts on change for animation) */}
          <div
            key={currentSlide}
            className="lp-slide-in lp-printable"
            style={{ width: '100%', maxWidth: 1280, flex: 1, display: 'flex', flexDirection: 'column' }}
          >
            {slide?.type === 'title' && <TitleSlide content={slide.content} isFr={isFr} dark={dark} color={meta.color} />}
            {slide?.type === 'vocab' && <VocabSlide content={slide.content} isFr={isFr} dark={dark} color={meta.color} />}
            {slide?.type === 'sentences' && <SentencesSlide content={slide.content} isFr={isFr} dark={dark} color={meta.color} />}
            {slide?.type === 'grammar' && <GrammarSlide content={slide.content} isFr={isFr} dark={dark} color={meta.color} />}
            {slide?.type === 'game_launch' && <GameLaunchSlide content={slide.content} isFr={isFr} dark={dark} navigate={navigate} currentSlide={currentSlide} lessonId={lessonId} />}
            {slide?.type === 'culture' && <CultureSlide content={slide.content} isFr={isFr} dark={dark} color={meta.color} />}
            {slide?.type === 'exit_check' && <ExitCheckSlide content={slide.content} isFr={isFr} dark={dark} color={meta.color} />}
          </div>
        </div>

        {/* Teacher Notes Panel */}
        {showNotes && (
          <div
            className="lp-no-print"
            style={{
              width: 340, flexShrink: 0,
              borderLeft: `1px solid ${border}`,
              background: dark ? 'rgba(120,53,15,0.15)' : '#FFFBEB',
              backdropFilter: 'blur(8px)',
              overflow: 'auto', padding: '1.1rem 1.2rem',
              position: 'relative', zIndex: 2,
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.9rem' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <StickyNote size={16} color={dark ? '#FCD34D' : '#92400E'} />
                <h3 style={{
                  fontFamily: 'Outfit, sans-serif', fontSize: '0.95rem', fontWeight: 700, margin: 0,
                  color: dark ? '#FCD34D' : '#92400E',
                }}>
                  {isFr ? 'Notes pédagogiques' : 'Teacher Notes'}
                </h3>
              </div>
              <button onClick={() => setShowNotes(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 2 }}>
                <X size={16} color={dark ? '#FCD34D' : '#92400E'} />
              </button>
            </div>
            {lesson.teacher_notes && (
              <div style={{ fontSize: '0.85rem', color: dark ? '#FDE68A' : '#78350F', lineHeight: 1.65 }}>
                {lesson.teacher_notes.before_teaching && (
                  <NoteBlock title={isFr ? 'Avant le cours' : 'Before teaching'} body={lesson.teacher_notes.before_teaching} dark={dark} />
                )}
                {lesson.teacher_notes.common_errors && (
                  <NoteBlock title={isFr ? 'Erreurs courantes' : 'Common errors'} body={lesson.teacher_notes.common_errors} dark={dark} />
                )}
                {lesson.teacher_notes.assessment_focus && (
                  <NoteBlock title={isFr ? "Focus d'évaluation" : 'Assessment focus'} body={lesson.teacher_notes.assessment_focus} dark={dark} />
                )}
                {lesson.teacher_notes.differentiation && (
                  <div style={{ marginTop: '0.75rem' }}>
                    <div style={{ fontWeight: 700, marginBottom: 4 }}>{isFr ? 'Différenciation' : 'Differentiation'}</div>
                    {lesson.teacher_notes.differentiation.ell && <p style={{ margin: '0.2rem 0' }}><strong>ELL:</strong> {lesson.teacher_notes.differentiation.ell}</p>}
                    {lesson.teacher_notes.differentiation.iep && <p style={{ margin: '0.2rem 0' }}><strong>IEP:</strong> {lesson.teacher_notes.differentiation.iep}</p>}
                    {lesson.teacher_notes.differentiation.gifted && <p style={{ margin: '0.2rem 0' }}><strong>{isFr ? 'Doués' : 'Gifted'}:</strong> {lesson.teacher_notes.differentiation.gifted}</p>}
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {/* ============== BOTTOM CHROME (nav) ============== */}
      <div
        className="lp-no-print"
        style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '0.7rem clamp(1rem, 3vw, 2rem)',
          background: dark ? 'rgba(15,20,35,0.7)' : 'rgba(255,255,255,0.8)',
          backdropFilter: 'blur(12px)',
          borderTop: `1px solid ${border}`,
          flexShrink: 0,
          transform: present && !chromeVisible ? 'translateY(100%)' : 'translateY(0)',
          transition: 'transform 0.28s ease',
          position: present ? 'absolute' : 'relative',
          bottom: 0, left: 0, right: 0, zIndex: 20,
        }}
      >
        <button
          data-testid="slide-prev-btn"
          onClick={goPrev}
          disabled={currentSlide === 0}
          style={{
            display: 'flex', alignItems: 'center', gap: 6, padding: '9px 18px',
            borderRadius: 10, border: `1px solid ${border}`,
            cursor: currentSlide === 0 ? 'not-allowed' : 'pointer',
            background: dark ? 'rgba(255,255,255,0.04)' : 'white',
            color: currentSlide === 0 ? (dark ? '#475569' : '#CBD5E1') : textMain,
            fontFamily: 'Nunito, sans-serif', fontSize: '0.88rem', fontWeight: 600,
            transition: 'all 0.15s',
          }}
        >
          <ArrowLeft size={16} /> {isFr ? 'Précédent' : 'Previous'}
        </button>

        <div style={{
          display: 'flex', alignItems: 'center', gap: 10,
          fontFamily: 'Nunito, sans-serif', fontSize: '0.82rem', color: textMuted,
        }}>
          <kbd style={kbdStyle(dark)}>←</kbd>
          <kbd style={kbdStyle(dark)}>→</kbd>
          <span>·</span>
          <kbd style={kbdStyle(dark)}>F</kbd>
          <span style={{ fontSize: '0.78rem' }}>{isFr ? 'Plein écran' : 'Fullscreen'}</span>
        </div>

        <button
          data-testid="slide-next-btn"
          onClick={goNext}
          disabled={currentSlide >= totalSlides - 1}
          style={{
            display: 'flex', alignItems: 'center', gap: 6, padding: '10px 20px',
            borderRadius: 10, border: 'none',
            cursor: currentSlide >= totalSlides - 1 ? 'not-allowed' : 'pointer',
            background: currentSlide >= totalSlides - 1
              ? (dark ? '#334155' : '#CBD5E1')
              : `linear-gradient(135deg, ${meta.color}, ${meta.color}E0)`,
            color: 'white', fontFamily: 'Nunito, sans-serif', fontSize: '0.9rem', fontWeight: 700,
            boxShadow: currentSlide >= totalSlides - 1 ? 'none' : `0 6px 18px -6px ${meta.color}80`,
            transition: 'all 0.15s',
          }}
        >
          {isFr ? 'Suivant' : 'Next'} <ArrowRight size={16} />
        </button>
      </div>
    </div>
  );
}

/* ============================================================
 * SLIDE RENDERERS
 * ============================================================ */

function TitleSlide({ content, isFr, dark, color }) {
  if (!content) return null;
  const title = isFr ? (content.title_fr || content.title_en) : (content.title_en || content.title_fr);
  const goals = (isFr ? (content.goals_fr || content.goals_en) : (content.goals_en || content.goals_fr)) || [];
  const script = isFr ? (content.teacher_script_fr || content.teacher_script_en) : (content.teacher_script_en || content.teacher_script_fr);
  const textMain = dark ? '#F8FAFC' : '#0F172A';
  const textMuted = dark ? '#CBD5E1' : '#475569';

  return (
    <div data-testid="slide-title" style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', paddingTop: '1rem' }}>
      {/* Hero title */}
      <h1 style={{
        fontFamily: 'Fredoka, sans-serif',
        fontSize: 'clamp(2.4rem, 6.5vw, 5.4rem)',
        fontWeight: 700, lineHeight: 1.05,
        color: textMain, margin: 0,
        textAlign: 'center',
        letterSpacing: '-0.015em',
      }}>
        {title}
      </h1>

      {/* Goals */}
      {goals.length > 0 && (
        <div style={{ marginTop: 'clamp(1.5rem, 3vw, 2.5rem)', display: 'flex', justifyContent: 'center' }}>
          <div style={{ maxWidth: 820, width: '100%' }}>
            <div style={{
              display: 'inline-flex', alignItems: 'center', gap: 8,
              fontSize: '0.85rem', fontWeight: 700, letterSpacing: '0.14em',
              textTransform: 'uppercase', color: color, marginBottom: '0.85rem',
            }}>
              {isFr ? "Objectifs d'aujourd'hui" : "Today's goals"}
            </div>
            <ul className="lp-stagger" style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: '0.7rem' }}>
              {goals.map((g, i) => (
                <li key={i} style={{
                  display: 'flex', alignItems: 'flex-start', gap: 14,
                  fontSize: 'clamp(1.05rem, 1.6vw, 1.45rem)',
                  fontFamily: 'Nunito, sans-serif', fontWeight: 500, color: textMain,
                  lineHeight: 1.45,
                }}>
                  <span style={{
                    flexShrink: 0, marginTop: 6,
                    width: 14, height: 14, borderRadius: 7,
                    background: color, boxShadow: `0 0 0 4px ${color}22`,
                  }} />
                  <span>{g}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}

      {/* Teacher script (stage whisper) */}
      {script && (
        <div style={{ marginTop: 'clamp(1.5rem, 3vw, 2.5rem)', display: 'flex', justifyContent: 'center' }}>
          <div className="lp-pop-in" style={{
            maxWidth: 820, width: '100%',
            background: dark ? 'rgba(16,185,129,0.12)' : '#F0FDF4',
            borderTop: `1px solid ${dark ? 'rgba(16,185,129,0.25)' : '#BBF7D0'}`,
            borderRight: `1px solid ${dark ? 'rgba(16,185,129,0.25)' : '#BBF7D0'}`,
            borderBottom: `1px solid ${dark ? 'rgba(16,185,129,0.25)' : '#BBF7D0'}`,
            borderLeft: `4px solid #16A34A`,
            borderRadius: 14, padding: '1.1rem 1.35rem',
            display: 'flex', gap: 14, alignItems: 'flex-start',
          }}>
            <Quote size={22} color="#16A34A" style={{ flexShrink: 0, marginTop: 2 }} />
            <div>
              <div style={{
                fontSize: '0.72rem', fontWeight: 700, letterSpacing: '0.1em',
                textTransform: 'uppercase', color: dark ? '#86EFAC' : '#15803D', marginBottom: 4,
              }}>
                {isFr ? 'Dites à la classe' : 'Say to the class'}
              </div>
              <p style={{
                margin: 0, fontSize: 'clamp(1rem, 1.5vw, 1.25rem)',
                color: dark ? '#BBF7D0' : '#14532D', fontStyle: 'italic',
                lineHeight: 1.55, fontFamily: 'Nunito, sans-serif',
              }}>
                {script}
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function VocabSlide({ content, isFr, dark, color }) {
  if (!content) return null;
  const words = content.words || [];
  const instruction = isFr ? (content.instruction_fr || content.instruction_en) : (content.instruction_en || content.instruction_fr);
  const textMain = dark ? '#F8FAFC' : '#0F172A';
  const textMuted = dark ? '#94A3B8' : '#64748B';
  const cardBg = dark ? 'rgba(255,255,255,0.04)' : 'white';
  const cardBorder = dark ? 'rgba(255,255,255,0.08)' : '#E2E8F0';

  return (
    <div data-testid="slide-vocab" style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
      {instruction && (
        <p style={{
          fontSize: 'clamp(1rem, 1.4vw, 1.15rem)', color: textMuted,
          fontFamily: 'Nunito, sans-serif', fontStyle: 'italic',
          margin: '0 0 clamp(1rem, 2vw, 1.75rem)', textAlign: 'center',
        }}>
          {instruction}
        </p>
      )}

      <div className="lp-stagger" style={{
        display: 'grid',
        gridTemplateColumns: `repeat(auto-fit, minmax(clamp(220px, 22vw, 280px), 1fr))`,
        gap: 'clamp(0.75rem, 1.5vw, 1.25rem)',
        flex: 1, alignContent: 'center',
      }}>
        {words.map((word, i) => {
          const genderColor = word.gender === 'm' ? '#1971C2' : word.gender === 'f' ? '#E64980' : null;
          return (
            <div
              key={i}
              className="lp-word-card"
              style={{
                background: cardBg,
                border: `1px solid ${cardBorder}`,
                borderTop: `4px solid ${genderColor || color}`,
                borderRadius: 16, padding: 'clamp(1rem, 1.8vw, 1.5rem)',
                display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center',
                minHeight: 160,
                backdropFilter: dark ? 'blur(8px)' : 'none',
              }}
            >
              {word.gender && (
                <span style={{
                  fontSize: '0.65rem', fontWeight: 800, letterSpacing: '0.14em',
                  color: genderColor,
                  background: genderColor + (dark ? '2A' : '16'),
                  borderRadius: 999, padding: '3px 10px', marginBottom: 10,
                }}>
                  {word.gender === 'm' ? (isFr ? 'MASCULIN' : 'MASCULINE') : (isFr ? 'FÉMININ' : 'FEMININE')}
                </span>
              )}
              <div style={{
                fontFamily: 'Fredoka, sans-serif',
                fontSize: 'clamp(1.4rem, 2.4vw, 2rem)',
                fontWeight: 600, color: textMain, lineHeight: 1.15,
                letterSpacing: '-0.01em',
              }}>
                {word.fr}
              </div>
              {word.en && (
                <div style={{
                  marginTop: 8,
                  fontSize: 'clamp(0.85rem, 1.1vw, 1rem)',
                  color: textMuted, fontFamily: 'Nunito, sans-serif', fontStyle: 'italic',
                }}>
                  {word.en}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function SentencesSlide({ content, isFr, dark, color }) {
  if (!content) return null;
  const sentences = content.sentences || [];
  const instruction = isFr ? (content.instruction_fr || content.instruction_en) : (content.instruction_en || content.instruction_fr);
  const textMain = dark ? '#F8FAFC' : '#0F172A';
  const textMuted = dark ? '#94A3B8' : '#64748B';
  const cardBg = dark ? 'rgba(255,255,255,0.04)' : 'white';
  const cardBorder = dark ? 'rgba(255,255,255,0.08)' : '#E2E8F0';

  return (
    <div data-testid="slide-sentences" style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
      {instruction && (
        <p style={{
          fontSize: 'clamp(1rem, 1.4vw, 1.15rem)', color: textMuted,
          fontFamily: 'Nunito, sans-serif', fontStyle: 'italic',
          margin: '0 0 clamp(1rem, 2vw, 1.75rem)', textAlign: 'center',
        }}>
          {instruction}
        </p>
      )}

      <div className="lp-stagger" style={{
        display: 'flex', flexDirection: 'column', gap: 'clamp(0.6rem, 1vw, 0.9rem)',
        flex: 1, justifyContent: 'center', maxWidth: 1060, width: '100%', margin: '0 auto',
      }}>
        {sentences.map((s, i) => (
          <div key={i} style={{
            display: 'flex', alignItems: 'flex-start', gap: 18,
            background: cardBg,
            borderTop: `1px solid ${cardBorder}`,
            borderRight: `1px solid ${cardBorder}`,
            borderBottom: `1px solid ${cardBorder}`,
            borderLeft: `4px solid ${color}`,
            borderRadius: 14, padding: 'clamp(0.9rem, 1.6vw, 1.25rem) clamp(1rem, 2vw, 1.5rem)',
            backdropFilter: dark ? 'blur(8px)' : 'none',
          }}>
            <div style={{
              flexShrink: 0,
              width: 36, height: 36, borderRadius: 10,
              background: color + (dark ? '2A' : '16'),
              color: color,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontFamily: 'Outfit, sans-serif', fontWeight: 700, fontSize: '0.95rem',
            }}>
              {i + 1}
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{
                fontFamily: 'Fredoka, sans-serif',
                fontSize: 'clamp(1.1rem, 1.9vw, 1.65rem)',
                fontWeight: 500, color: textMain, lineHeight: 1.3,
              }}>
                {s.fr}
              </div>
              {s.en && (
                <div style={{
                  marginTop: 4,
                  fontSize: 'clamp(0.85rem, 1.1vw, 1rem)',
                  color: textMuted, fontFamily: 'Nunito, sans-serif', fontStyle: 'italic',
                }}>
                  {s.en}
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function GrammarSlide({ content, isFr, dark, color }) {
  if (!content) return null;
  const title = isFr ? (content.rule_title_fr || content.rule_title_en) : (content.rule_title_en || content.rule_title_fr);
  const explanation = isFr ? (content.rule_explanation_fr || content.rule_explanation_en) : (content.rule_explanation_en || content.rule_explanation_fr);
  const examples = content.examples || [];
  const textMain = dark ? '#F8FAFC' : '#0F172A';
  const textMuted = dark ? '#CBD5E1' : '#475569';
  const cardBg = dark ? 'rgba(255,255,255,0.04)' : 'white';
  const cardBorder = dark ? 'rgba(255,255,255,0.08)' : '#FED7AA';

  return (
    <div data-testid="slide-grammar" style={{
      flex: 1,
      display: 'grid',
      gridTemplateColumns: examples.length > 0 ? 'minmax(0, 1.1fr) minmax(0, 1fr)' : '1fr',
      gap: 'clamp(1.25rem, 3vw, 2.5rem)',
      alignItems: 'center',
    }}>
      {/* Rule */}
      <div>
        <h2 style={{
          fontFamily: 'Outfit, sans-serif',
          fontSize: 'clamp(1.6rem, 3.2vw, 2.6rem)',
          fontWeight: 800, color: color, margin: '0 0 0.8rem',
          lineHeight: 1.15, letterSpacing: '-0.01em',
        }}>
          {title}
        </h2>
        <p style={{
          fontFamily: 'Nunito, sans-serif',
          fontSize: 'clamp(1rem, 1.5vw, 1.3rem)',
          color: textMuted, lineHeight: 1.55, margin: 0,
        }}>
          {explanation}
        </p>
        {content.visual_note && (
          <div style={{
            marginTop: '1.25rem',
            padding: '0.9rem 1.1rem',
            background: dark ? 'rgba(234,88,12,0.15)' : '#FFF7ED',
            border: `1px solid ${dark ? 'rgba(234,88,12,0.3)' : '#FED7AA'}`,
            borderRadius: 12,
            fontSize: 'clamp(0.88rem, 1.2vw, 1.02rem)',
            color: dark ? '#FDBA74' : '#9A3412',
            fontFamily: 'Nunito, sans-serif',
          }}>
            💡 {content.visual_note}
          </div>
        )}
      </div>

      {/* Examples */}
      {examples.length > 0 && (
        <div className="lp-stagger" style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
          <div style={{
            fontSize: '0.82rem', fontWeight: 700, letterSpacing: '0.14em',
            textTransform: 'uppercase', color: color, marginBottom: 4,
          }}>
            {isFr ? 'Exemples' : 'Examples'}
          </div>
          {examples.map((ex, i) => (
            <div key={i} style={{
              background: cardBg,
              borderTop: `1px solid ${cardBorder}`,
              borderRight: `1px solid ${cardBorder}`,
              borderBottom: `1px solid ${cardBorder}`,
              borderLeft: `4px solid ${color}`,
              borderRadius: 12, padding: 'clamp(0.85rem, 1.4vw, 1.1rem) 1.15rem',
              backdropFilter: dark ? 'blur(8px)' : 'none',
            }}>
              <div style={{
                fontFamily: 'Fredoka, sans-serif',
                fontSize: 'clamp(1.05rem, 1.5vw, 1.3rem)',
                fontWeight: 500, color: textMain,
              }}>
                {ex.fr}
              </div>
              {ex.en && (
                <div style={{
                  marginTop: 3,
                  fontSize: 'clamp(0.82rem, 1.05vw, 0.95rem)',
                  color: textMuted, fontStyle: 'italic', fontFamily: 'Nunito, sans-serif',
                }}>
                  {ex.en}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function GameLaunchSlide({ content, isFr, dark, navigate, currentSlide, lessonId }) {
  const [contentAvailable, setContentAvailable] = useState(null);
  
  const gameId = content?.game_id;
  const presetConfig = content?.preset_config;
  
  useEffect(() => {
    if (!gameId) return;
    const pc = presetConfig || {};
    const params = { mode: pc.mode || 'level', game: gameId, limit: 1 };
    if (pc.level) params.level = pc.level;
    if (pc.topic) params.topic = pc.topic;
    if (pc.difficulty) params.difficulty = pc.difficulty;
    axios.get(`${API}/content/browse`, { params })
      .then(r => setContentAvailable((r.data.count || 0) > 0))
      .catch(() => setContentAvailable(true));
  }, [gameId, presetConfig]);
  
  if (!content) return null;
  
  const emoji = GAME_EMOJI[content.game_id] || '🎮';
  const name = GAME_NAMES[content.game_id] || content.game_id;
  const textMain = dark ? '#F8FAFC' : '#0F172A';
  const textMuted = dark ? '#CBD5E1' : '#64748B';
  const launchColor = '#C92A2A';

  const handleLaunch = () => {
    const pc = content.preset_config || {};
    const params = new URLSearchParams();
    if (pc.mode) params.set('mode', pc.mode);
    if (pc.level) params.set('level', pc.level);
    if (pc.topic) params.set('topic', pc.topic);
    if (pc.difficulty) params.set('difficulty', pc.difficulty);
    if (pc.match_type) params.set('matchType', pc.match_type);
    // Pass game config from lesson slide
    if (content.target_score) params.set('target', String(content.target_score));
    if (content.timer_minutes) params.set('gameTimer', String(content.timer_minutes * 60));
    if (content.item_count) params.set('lessonItemCount', String(content.item_count));
    if (content.suggested_team_names?.length >= 2) {
      params.set('team1Name', content.suggested_team_names[0]);
      params.set('team2Name', content.suggested_team_names[1]);
    }
    // Signal lesson-bank mode so game fetches from dedicated bank
    params.set('lessonBank', '1');
    params.set('lessonId', lessonId);
    const returnPath = window.location.pathname;
    params.set('returnTo', returnPath);
    params.set('returnSlide', String(currentSlide + 1));
    // Record the current (game-launch) slide on the lesson's own history entry so a browser/back
    // gesture out of the game restores this slide instead of resetting the lesson to slide 0.
    navigate(`${returnPath}?slide=${currentSlide}`, { replace: true });
    navigate(`/play/${content.game_id}/setup?${params.toString()}`);
  };

  const script = isFr ? (content.facilitation_note_fr || content.facilitation_note_en) : (content.facilitation_note_en || content.facilitation_note_fr);

  return (
    <div data-testid="slide-game-launch" style={{
      flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center',
    }}>
      {/* Emoji orb */}
      <div style={{ position: 'relative', marginBottom: 'clamp(1rem, 2vw, 1.5rem)' }}>
        <div
          aria-hidden
          style={{
            position: 'absolute', inset: '-30%',
            background: `radial-gradient(circle, ${launchColor}${dark ? '40' : '22'}, transparent 65%)`,
            filter: 'blur(12px)',
          }}
        />
        <div className="lp-float" style={{
          fontSize: 'clamp(4.5rem, 10vw, 8rem)',
          position: 'relative', lineHeight: 1,
        }}>
          {emoji}
        </div>
      </div>

      {/* Game name */}
      <h2 style={{
        fontFamily: 'Fredoka, sans-serif',
        fontSize: 'clamp(2.5rem, 6vw, 5rem)',
        fontWeight: 700, color: textMain, margin: 0, lineHeight: 1.02,
        letterSpacing: '-0.02em',
      }}>
        {name}
      </h2>

      {/* Preset pills */}
      <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: 8, marginTop: 'clamp(1rem, 1.8vw, 1.5rem)' }}>
        {content.preset_config?.level && (
          <span style={{
            fontSize: 'clamp(0.85rem, 1.1vw, 0.98rem)', fontWeight: 700,
            color: CEFR_COLORS[content.preset_config.level] || textMuted,
            background: (CEFR_COLORS[content.preset_config.level] || textMuted) + (dark ? '30' : '18'),
            borderRadius: 999, padding: '6px 14px',
          }}>
            {content.preset_config.level}
          </span>
        )}
        {content.preset_config?.topic && (
          <span style={{
            fontSize: 'clamp(0.85rem, 1.1vw, 0.98rem)',
            color: textMuted,
            background: dark ? 'rgba(255,255,255,0.06)' : '#F1F5F9',
            borderRadius: 999, padding: '6px 14px', textTransform: 'capitalize',
          }}>
            {content.preset_config.topic.replace(/_/g, ' ')}
          </span>
        )}
        {content.item_count && (
          <span style={{
            fontSize: 'clamp(0.85rem, 1.1vw, 0.98rem)',
            color: textMuted,
            background: dark ? 'rgba(255,255,255,0.06)' : '#F1F5F9',
            borderRadius: 999, padding: '6px 14px',
          }}>
            {content.item_count} {isFr ? 'éléments' : 'items'}
          </span>
        )}
      </div>

      {/* Launch button or content-unavailable message */}
      {contentAvailable === false ? (
        <div data-testid="game-content-unavailable" style={{
          marginTop: 'clamp(1.5rem, 3vw, 2.5rem)',
          padding: '16px 32px', borderRadius: 16,
          background: dark ? 'rgba(255,255,255,0.06)' : '#F8FAFC',
          border: `1px solid ${dark ? 'rgba(255,255,255,0.1)' : '#E2E8F0'}`,
          color: textMuted, fontFamily: 'Nunito, sans-serif',
          fontSize: 'clamp(0.95rem, 1.3vw, 1.1rem)', textAlign: 'center',
        }}>
          {isFr ? 'Contenu bientôt disponible pour ce jeu.' : 'Content coming soon for this game.'}
        </div>
      ) : (
        <button
          data-testid="game-launch-btn"
          onClick={handleLaunch}
          className="lp-launch-pulse"
          disabled={contentAvailable === null}
          style={{
            marginTop: 'clamp(1.5rem, 3vw, 2.5rem)',
            padding: '16px 44px', borderRadius: 999, border: 'none',
            background: contentAvailable === null
              ? (dark ? '#334155' : '#CBD5E1')
              : `linear-gradient(135deg, ${launchColor}, #E03131)`,
            color: 'white', fontFamily: 'Fredoka, sans-serif',
            fontSize: 'clamp(1.1rem, 1.6vw, 1.4rem)', fontWeight: 600,
            letterSpacing: '0.02em',
            cursor: contentAvailable === null ? 'wait' : 'pointer',
            display: 'inline-flex', alignItems: 'center', gap: 10,
            transition: 'transform 0.12s',
            opacity: contentAvailable === null ? 0.6 : 1,
          }}
          onMouseEnter={e => { if (contentAvailable) e.currentTarget.style.transform = 'scale(1.04)'; }}
          onMouseLeave={e => { e.currentTarget.style.transform = 'scale(1)'; }}
        >
          <Play size={22} fill="white" />
          {isFr ? 'Lancer le jeu' : 'Launch Game'}
        </button>
      )}

      {/* Facilitation tip */}
      {script && (
        <div className="lp-pop-in" style={{
          maxWidth: 700, marginTop: 'clamp(1.5rem, 2.5vw, 2rem)',
          background: dark ? 'rgba(16,185,129,0.12)' : '#F0FDF4',
          borderTop: `1px solid ${dark ? 'rgba(16,185,129,0.25)' : '#BBF7D0'}`,
          borderRight: `1px solid ${dark ? 'rgba(16,185,129,0.25)' : '#BBF7D0'}`,
          borderBottom: `1px solid ${dark ? 'rgba(16,185,129,0.25)' : '#BBF7D0'}`,
          borderLeft: '4px solid #16A34A',
          borderRadius: 12, padding: '0.9rem 1.1rem',
          display: 'flex', gap: 12, alignItems: 'flex-start', textAlign: 'left',
        }}>
          <Quote size={18} color="#16A34A" style={{ flexShrink: 0, marginTop: 2 }} />
          <div style={{
            fontSize: 'clamp(0.88rem, 1.15vw, 1rem)',
            color: dark ? '#BBF7D0' : '#14532D',
            lineHeight: 1.5, fontFamily: 'Nunito, sans-serif',
          }}>
            <strong style={{ fontWeight: 700 }}>{isFr ? 'Conseil : ' : 'Tip: '}</strong>
            {script}
          </div>
        </div>
      )}

      {content.suggested_team_names && content.suggested_team_names.length > 0 && (
        <p style={{
          marginTop: '1rem', fontSize: '0.92rem', color: textMuted,
          fontFamily: 'Nunito, sans-serif',
        }}>
          {isFr ? 'Équipes suggérées : ' : 'Suggested teams: '}
          <strong style={{ color: textMain }}>{content.suggested_team_names.join(' vs ')}</strong>
        </p>
      )}
    </div>
  );
}

function CultureSlide({ content, isFr, dark, color }) {
  if (!content) return null;
  const title = isFr ? (content.title_fr || content.title_en) : (content.title_en || content.title_fr);
  const body = isFr ? (content.content_fr || content.content_en) : (content.content_en || content.content_fr);
  const indigenous = isFr ? (content.indigenous_content_fr || content.indigenous_content_en) : (content.indigenous_content_en || content.indigenous_content_fr);
  const textMain = dark ? '#F8FAFC' : '#0F172A';
  const textMuted = dark ? '#CBD5E1' : '#475569';

  return (
    <div data-testid="slide-culture" style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ maxWidth: 920, width: '100%' }}>
        <h2 style={{
          fontFamily: 'Outfit, sans-serif',
          fontSize: 'clamp(1.8rem, 3.5vw, 2.8rem)',
          fontWeight: 800, color: color, margin: '0 0 clamp(0.75rem, 1.5vw, 1.1rem)',
          lineHeight: 1.15, letterSpacing: '-0.015em',
        }}>
          {title}
        </h2>

        {body && (
          <div style={{
            fontFamily: 'Nunito, sans-serif',
            fontSize: 'clamp(1rem, 1.5vw, 1.3rem)',
            color: textMuted, lineHeight: 1.65, margin: 0,
          }}>
            {body}
          </div>
        )}

        {content.has_indigenous_content && indigenous && (
          <div className="lp-pop-in" style={{
            marginTop: 'clamp(1.25rem, 2.5vw, 2rem)',
            background: dark ? 'rgba(217,119,6,0.15)' : '#FFFBEB',
            borderTop: `1px solid ${dark ? 'rgba(217,119,6,0.35)' : '#FDE68A'}`,
            borderRight: `1px solid ${dark ? 'rgba(217,119,6,0.35)' : '#FDE68A'}`,
            borderBottom: `1px solid ${dark ? 'rgba(217,119,6,0.35)' : '#FDE68A'}`,
            borderLeft: '4px solid #D97706',
            borderRadius: 14, padding: 'clamp(1rem, 1.8vw, 1.4rem) clamp(1.1rem, 2vw, 1.6rem)',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
              <Feather size={18} color={dark ? '#FCD34D' : '#B45309'} />
              <span style={{
                fontSize: '0.8rem', fontWeight: 700, letterSpacing: '0.14em',
                textTransform: 'uppercase', color: dark ? '#FCD34D' : '#B45309',
              }}>
                {isFr ? 'Perspectives autochtones' : 'Indigenous Perspectives'}
              </span>
            </div>
            <p style={{
              margin: 0,
              fontSize: 'clamp(0.95rem, 1.3vw, 1.15rem)',
              color: dark ? '#FDE68A' : '#78350F',
              lineHeight: 1.6, fontFamily: 'Nunito, sans-serif',
            }}>
              {indigenous}
            </p>
            {content.nations_referenced && content.nations_referenced.length > 0 && (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 12 }}>
                {content.nations_referenced.map((n, i) => (
                  <span key={i} style={{
                    fontSize: '0.78rem', fontWeight: 600,
                    color: dark ? '#FCD34D' : '#92400E',
                    background: dark ? 'rgba(252,211,77,0.12)' : '#FEF3C7',
                    border: `1px solid ${dark ? 'rgba(252,211,77,0.3)' : '#FDE68A'}`,
                    borderRadius: 999, padding: '3px 10px',
                  }}>
                    {n}
                  </span>
                ))}
              </div>
            )}
          </div>
        )}

        {content.source && (
          <p style={{
            marginTop: '1rem', fontSize: '0.8rem', color: textMuted,
            fontStyle: 'italic', fontFamily: 'Nunito, sans-serif',
          }}>
            {content.source}
          </p>
        )}
      </div>
    </div>
  );
}

function ExitCheckSlide({ content, isFr, dark, color }) {
  const [revealed, setRevealed] = useState({});
  if (!content) return null;
  const questions = content.questions || [];
  const textMain = dark ? '#F8FAFC' : '#0F172A';
  const textMuted = dark ? '#94A3B8' : '#64748B';
  const cardBg = dark ? 'rgba(255,255,255,0.04)' : 'white';
  const cardBorder = dark ? 'rgba(255,255,255,0.08)' : '#E2E8F0';

  return (
    <div data-testid="slide-exit-check" style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
      <div className="lp-stagger" style={{
        display: 'flex', flexDirection: 'column', gap: 'clamp(0.7rem, 1.3vw, 1rem)',
        flex: 1, maxWidth: 1060, width: '100%', margin: '0 auto',
      }}>
        {questions.map((q, i) => {
          const isRevealed = !!revealed[i];
          return (
            <div key={i} style={{
              background: cardBg,
              borderTop: `1px solid ${cardBorder}`,
              borderRight: `1px solid ${cardBorder}`,
              borderBottom: `1px solid ${cardBorder}`,
              borderLeft: `4px solid ${color}`,
              borderRadius: 14, padding: 'clamp(1rem, 1.6vw, 1.4rem) clamp(1.1rem, 2vw, 1.6rem)',
              backdropFilter: dark ? 'blur(8px)' : 'none',
            }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 16 }}>
                <div style={{
                  flexShrink: 0,
                  width: 42, height: 42, borderRadius: 999,
                  background: color + (dark ? '2A' : '16'),
                  color: color,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontFamily: 'Outfit, sans-serif', fontWeight: 800, fontSize: '1.05rem',
                }}>
                  {i + 1}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <p style={{
                    margin: 0,
                    fontFamily: 'Fredoka, sans-serif',
                    fontSize: 'clamp(1.1rem, 1.7vw, 1.55rem)',
                    fontWeight: 500, color: textMain, lineHeight: 1.3,
                  }}>
                    {isFr ? (q.question_fr || q.question_en) : (q.question_en || q.question_fr)}
                  </p>
                  {q.category && (
                    <span style={{
                      fontSize: '0.72rem', color: textMuted,
                      background: dark ? 'rgba(255,255,255,0.06)' : '#F1F5F9',
                      borderRadius: 6, padding: '2px 8px', marginTop: 6, display: 'inline-block',
                      textTransform: 'capitalize',
                    }}>
                      {q.category}
                    </span>
                  )}
                </div>
                <button
                  data-testid={`reveal-answer-${i}`}
                  onClick={() => setRevealed(prev => ({ ...prev, [i]: !prev[i] }))}
                  style={{
                    flexShrink: 0, alignSelf: 'center',
                    display: 'flex', alignItems: 'center', gap: 6,
                    padding: '8px 14px', borderRadius: 10,
                    border: `1px solid ${isRevealed ? (dark ? 'rgba(22,163,74,0.4)' : '#BBF7D0') : cardBorder}`,
                    background: isRevealed ? (dark ? 'rgba(22,163,74,0.15)' : '#F0FDF4') : (dark ? 'rgba(255,255,255,0.04)' : 'white'),
                    color: isRevealed ? (dark ? '#86EFAC' : '#15803D') : textMuted,
                    fontSize: '0.82rem', fontFamily: 'Nunito, sans-serif',
                    fontWeight: 600, cursor: 'pointer', transition: 'all 0.15s',
                  }}
                >
                  {isRevealed ? <EyeOff size={14} /> : <Eye size={14} />}
                  {isRevealed ? (isFr ? 'Masquer' : 'Hide') : (isFr ? 'Réponse' : 'Answer')}
                </button>
              </div>
              {isRevealed && q.answer && (
                <div className="lp-pop-in" style={{
                  marginTop: 12, marginLeft: 58,
                  padding: '0.8rem 1.1rem',
                  background: dark ? 'rgba(22,163,74,0.12)' : '#F0FDF4',
                  border: `1px solid ${dark ? 'rgba(22,163,74,0.3)' : '#BBF7D0'}`,
                  borderRadius: 10,
                  fontFamily: 'Fredoka, sans-serif',
                  fontSize: 'clamp(1rem, 1.45vw, 1.3rem)',
                  color: dark ? '#86EFAC' : '#15803D',
                  fontWeight: 500,
                }}>
                  {q.answer}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {content.defi_fr && (
        <div className="lp-pop-in" style={{
          marginTop: 'clamp(1rem, 2vw, 1.5rem)',
          maxWidth: 1060, margin: 'clamp(1rem, 2vw, 1.5rem) auto 0',
          background: dark ? 'rgba(124,58,237,0.18)' : '#F5F3FF',
          borderTop: `1px solid ${dark ? 'rgba(124,58,237,0.35)' : '#DDD6FE'}`,
          borderRight: `1px solid ${dark ? 'rgba(124,58,237,0.35)' : '#DDD6FE'}`,
          borderBottom: `1px solid ${dark ? 'rgba(124,58,237,0.35)' : '#DDD6FE'}`,
          borderLeft: '4px solid #7C3AED',
          borderRadius: 14, padding: '1rem 1.3rem',
          display: 'flex', gap: 12, alignItems: 'flex-start',
        }}>
          <div>
            <div style={{
              fontSize: '0.75rem', fontWeight: 700, letterSpacing: '0.12em',
              textTransform: 'uppercase', color: dark ? '#C4B5FD' : '#6D28D9', marginBottom: 4,
            }}>
              {isFr ? 'Défi' : 'Challenge'}
            </div>
            <p style={{
              margin: 0,
              fontSize: 'clamp(0.95rem, 1.3vw, 1.15rem)',
              color: dark ? '#DDD6FE' : '#5B21B6',
              fontFamily: 'Nunito, sans-serif', lineHeight: 1.55,
            }}>
              {isFr ? (content.defi_fr || content.defi_en) : (content.defi_en || content.defi_fr)}
            </p>
          </div>
        </div>
      )}
    </div>
  );
}

/* ============================================================
 * Helpers
 * ============================================================ */

function ChromeButton({ children, onClick, title, active, dark, testid }) {
  return (
    <button
      data-testid={testid}
      onClick={onClick}
      title={title}
      style={{
        display: 'flex', alignItems: 'center', gap: 6,
        padding: '6px 11px', borderRadius: 8,
        border: `1px solid ${dark ? 'rgba(255,255,255,0.1)' : '#E2E8F0'}`,
        cursor: 'pointer',
        background: active
          ? (dark ? 'rgba(255,255,255,0.08)' : '#F1F5F9')
          : (dark ? 'rgba(255,255,255,0.02)' : 'white'),
        color: dark ? '#E2E8F0' : '#475569',
        fontFamily: 'Nunito, sans-serif',
        transition: 'all 0.12s',
      }}
    >
      {children}
    </button>
  );
}

function NoteBlock({ title, body, dark }) {
  return (
    <div style={{ marginBottom: '0.85rem' }}>
      <div style={{ fontWeight: 700, marginBottom: 3 }}>{title}</div>
      <p style={{ margin: 0, color: dark ? '#FDE68A' : '#78350F' }}>{body}</p>
    </div>
  );
}

function kbdStyle(dark) {
  return {
    fontFamily: 'Outfit, sans-serif', fontSize: '0.72rem', fontWeight: 700,
    padding: '2px 7px', borderRadius: 6,
    border: `1px solid ${dark ? 'rgba(255,255,255,0.12)' : '#E2E8F0'}`,
    background: dark ? 'rgba(255,255,255,0.04)' : 'white',
    color: dark ? '#CBD5E1' : '#475569',
  };
}
